import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, NgForm, Validators } from '@angular/forms';
import { AppService } from '@services/app.service'; // Assuming these services are still needed for other component logic
import { AuthService } from '@services/auth.service'; // Assuming these services are still needed for other component logic
import { DateTime } from 'luxon';
import { CommonService } from '@modules/shared/services/common.service';
import { Router } from '@angular/router';
import { LoaderService } from '@modules/shared/services/loader.service';
import { ToastrService } from 'ngx-toastr';
import { getCurrentUser, updatePassword } from 'aws-amplify/auth';

@Component({
    selector: 'app-user',
    templateUrl: './user.component.html',
    styleUrls: ['./user.component.scss']
})
export class UserComponent implements OnInit {
    public userData: any; // Use 'any' or a more specific interface if you have one
    public isModalOpen = false;
    forgotPasswordForm: FormGroup;
    resetPasswordForm: FormGroup;
    public errorMessage: string = ''; // Added for displaying error messages
    public successMessage: string = ''; // Added for displaying success messages
    showOldPassword = false;
    showNewPassword = false;
    showConfirmPassword = false;
    showResetForm = false; 

    // 👇 Reference to pf-dropdown
    @ViewChild('userDropdown') userDropdown: any;

    constructor(
        private appService: AppService,
        private authservice: AuthService,
        private commonService: CommonService,
        private router: Router,
        private loaderService: LoaderService,
        private toast: ToastrService,
        private fb: FormBuilder
    ) { }

    ngOnInit(): void {
        this.userData = this.commonService.getItem('user_data');

        this.forgotPasswordForm = this.fb.group({
            email: ['', [Validators.required, Validators.email]]
        });

        this.resetPasswordForm = this.fb.group({
            email: ['', [Validators.required, Validators.email]],
            code: ['', Validators.required],
            new_password: [
                '',
                [
                    Validators.required,
                    Validators.minLength(8),
                    Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/)
                ]
            ],
            confirm_password: ['', Validators.required]
        }, { validators: this.passwordMatchValidator });
    }

    // Custom validator for confirm password
    passwordMatchValidator(control: AbstractControl) {
        const newPassword = control.get('new_password')?.value;
        const confirmPassword = control.get('confirm_password')?.value;
        return newPassword === confirmPassword ? null : { passwordMismatch: true };
    }

    openChangePasswordModal(): void {
        if (this.userDropdown && typeof this.userDropdown.close === 'function') {
            this.userDropdown.close();
        }
        this.isModalOpen = true;
        this.showResetForm = false;   // 👈 reset step
        this.forgotPasswordForm.reset();
        this.resetPasswordForm.reset();
    }

    closeChangePasswordModal(): void {
        this.isModalOpen = false;
        this.showResetForm = false;
    }

    // 👉 Call forgot password API
    onSubmitForgotPassword() {
        if (this.forgotPasswordForm.invalid) return;

        this.loaderService.showLoader();
        const email = this.forgotPasswordForm.value.email;

        this.authservice.forgotPassword(email).subscribe({
            next: (res: any) => {
                this.loaderService.hideLoader();
                this.toast.success('OTP sent to your email');
                this.showResetForm = true;
                this.resetPasswordForm.patchValue({ email });
            },
            error: (err) => {
                this.loaderService.hideLoader();
                this.toast.error(err.error?.details?.error || 'Failed to send OTP');
            }
        });
    }

    // 👉 Call reset password API
    onSubmitResetPassword() {
        if (this.resetPasswordForm.invalid) return;

        this.loaderService.showLoader();
        const { email, code, new_password } = this.resetPasswordForm.value;

        this.authservice.resetPassword({ email, code, new_password }).subscribe({
            next: (res: any) => {
                this.loaderService.hideLoader();
                this.toast.success('Password reset successfully');
                this.closeChangePasswordModal();
            },
            error: (err) => {
                this.loaderService.hideLoader();
                this.toast.error(err.error?.message || 'Failed to reset password');
            }
        });
    }

    togglePassword(field: string) {
        if (field === 'old') {
            this.showOldPassword = !this.showOldPassword;
        } else if (field === 'new') {
            this.showNewPassword = !this.showNewPassword;
        } else if (field === 'confirm') {
            this.showConfirmPassword = !this.showConfirmPassword;
        }
    }

    logout() {
        this.loaderService.showLoader();
        this.authservice.logoutAdmin().subscribe({
            next: (res: any) => {
                if (res.status_code == 200) {
                    this.toast.success(res.message);
                    this.commonService.removeItem('user_data');
                    this.commonService.removeItem('access_token');
                    this.commonService.removeItem('previousPath');
                    this.loaderService.hideLoader();
                    this.router.navigate(['/login']);
                } else {
                    this.toast.error(res.message);
                    this.loaderService.hideLoader();
                }
            },
            error: (err) => {
                console.error('Logout failed', err);
                this.toast.error(err.message);
                this.loaderService.hideLoader();
            }
        });
    }

    formatDate(date: string) { // Added type for date parameter
        return DateTime.fromISO(date).toFormat('dd LLL yyyy');
    }
}